/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MdmProblem.module.scss';

export const MdmProblem = () => {
  const { t } = useInt();
  const listRef = useRef<HTMLDivElement>(null);
  const hasIntersected = useIntersection(listRef, { threshold: 0.1 });

  const problems = [
    {
      num: '01',
      title: t('A company name is not an identifier'),
      desc: (
        <>
          {t(
            '"Acme Ltd", "ACME LIMITED", and "Acme Mfg." are one company to a human and three to a database. Keying identity on a string',
          )}{' '}
          <strong>{t('guarantees drift')}</strong>.
        </>
      ),
    },
    {
      num: '02',
      title: t('Every system onboarded the company separately'),
      desc: (
        <>
          {t(
            'Sales, procurement, billing, and compliance each created their own record with its own spelling and ID. Without a shared key, one customer is',
          )}{' '}
          <strong>{t('counted four times and governed zero times')}</strong>.
        </>
      ),
    },
    {
      num: '03',
      title: t('Records decay after onboarding'),
      desc: (
        <>
          {t(
            'Companies change name, address, directors, and status. A record that was right at onboarding quietly becomes wrong — and re-cleansing projects only',
          )}{' '}
          <strong>{t('reset the clock')}</strong>.
        </>
      ),
    },
    {
      num: '04',
      title: t('Enrichment arrives with no source attached'),
      desc: (
        <>
          {t(
            'A vendor appends revenue and headcount, but not the filing it came from or when it was read. The field can inform an opinion but',
          )}{' '}
          <strong>{t('cannot support an audit')}</strong>.
        </>
      ),
    },
    {
      num: '05',
      title: t("Hierarchy is missing, so roll-ups don't add up"),
      desc: (
        <>
          {t(
            'Spend, exposure, and risk sit at the legal entity but are managed at the group. When parent-child linkage is missing or stale,',
          )}{' '}
          <strong>{t("the ultimate parent's true exposure never rolls up")}</strong>.
        </>
      ),
    },
    {
      num: '06',
      title: t("Match decisions can't be interrogated"),
      desc: (
        <>
          {t(
            "A black-box score says 87 and nothing more. Stewards can't see which attributes agreed, so they review everything by hand — which is where",
          )}{' '}
          <strong>{t('most match automation quietly dies')}</strong>.
        </>
      ),
    },
  ];

  return (
    <Section className={styles.root}>
      <div className={styles.glow} aria-hidden="true" />

      <div className={styles.grid}>
        <div className={styles.rail}>
          <TitleBlock
            title={t('Company master data rarely fails loudly.')}
            description={t(
              "It degrades quietly — a near-duplicate here, a stale status there, a field nobody can trace. You feel it when finance can't roll up spend to a parent, or an auditor asks where a number came from and the file has no answer.",
            )}
            align="left"
          />

          <div className={styles.throughLine}>
            <span className={styles.throughLineLabel}>{t('The through-line')}</span>
            {t('Every one of these traces back to one thing — a company identified by a')}{' '}
            <strong>{t('name, not a number')}</strong>.
          </div>
        </div>

        <div ref={listRef} className={styles.list}>
          {problems.map((p, i) => (
            <div
              key={p.num}
              className={cn(styles.item, hasIntersected && styles.itemIn)}
              style={{ '--i': i } as CSSProperties}
            >
              <div className={styles.num}>{p.num}</div>
              <div>
                <h3 className={styles.itemTitle}>{p.title}</h3>
                <p className={styles.itemDesc}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
